import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { roomService } from '../services/roomService';
import toast from 'react-hot-toast';

const { FiLogIn, FiLoader, FiEye, FiEyeOff } = FiIcons;

const RoomJoin = ({ onJoinRoom }) => {
  const [formData, setFormData] = useState({ roomName: '', password: '' });
  const [isJoining, setIsJoining] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.roomName.trim() || !formData.password.trim()) return;
    
    setIsJoining(true);
    try {
      const result = await roomService.verifyRoom(formData.roomName.trim(), formData.password.trim());
      if (result.success) {
        onJoinRoom(formData.roomName.trim(), formData.password.trim());
      } else {
        toast.error(result.error || 'Invalid credentials');
      }
    } catch (error) {
      toast.error('Connection failed');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto border border-gray-100"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiLogIn} className="w-8 h-8 text-primary-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Room</h3>
        <p className="text-gray-600">Enter credentials to connect instantly</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">Room Name</label>
          <input 
            type="text" 
            value={formData.roomName}
            onChange={(e) => setFormData({ ...formData, roomName: e.target.value })}
            placeholder="e.standard CosmicCloud123" 
            className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
            disabled={isJoining}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">Password</label>
          <div className="relative">
            <input 
              type={showPassword ? 'text' : 'password'} 
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter 10-digit password" 
              className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
              disabled={isJoining}
            />
            <button 
              type="button" 
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 transition-colors"
            >
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <motion.button 
          type="submit" 
          disabled={isJoining || !formData.roomName || !formData.password}
          className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
          whileHover={{ scale: 1.02, backgroundColor: '#1d4ed8' }}
          whileTap={{ scale: 0.98 }}
        >
          {isJoining ? (
            <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <SafeIcon icon={FiLogIn} className="w-5 h-5" />
              Join Room
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default RoomJoin;